import React from 'react';
import { Card, CardContent, CardHeader, Divider, List, ListItem, ListItemText, Stack, Typography, Link as MuiLink } from '@mui/material';
import { ExposureSnapshot } from '../../canvas/components/ExposureSnapshot.js';
import { resolveInstrumentId, type ExposureHistoryEntry } from '../../../services/secondOrder.js';
import { useExposureEntry } from '../../canvas/hooks/useExposureEntry.js';

interface Props {
  profileId?: string | null;
  ticker?: string | null;
  instrumentId?: string | null;
}

interface FilingEntry {
  id: string;
  form: string;
  filedAt?: string | null;
  filedAtLabel?: string | null;
  url?: string;
  reference?: string | null;
  sortKey: number | null;
  version?: string;
}

interface VersionSummary {
  version: string;
  updatedAt?: string | null;
  updatedAtLabel?: string | null;
  filings: FilingEntry[];
}

interface NormalizedFiling extends FilingEntry {
  dedupeKey: string;
  versions: string[];
}

const formatFilingDate = (value?: string | null): string | null => {
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatVersionTimestamp = (value?: string | null): string | null => {
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const parseSecSource = (raw?: string | null): FilingEntry | null => {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.toLowerCase().startsWith('sec:')) return null;
  const remainder = trimmed.slice(4);
  const firstSep = remainder.indexOf(':');
  if (firstSep === -1) return null;
  const form = remainder.slice(0, firstSep).toUpperCase();
  const afterForm = remainder.slice(firstSep + 1);
  if (!afterForm) return null;
  const secondSep = afterForm.indexOf(':');
  let filedAt: string | null = null;
  let tail = afterForm;
  if (secondSep !== -1) {
    filedAt = afterForm.slice(0, secondSep) || null;
    tail = afterForm.slice(secondSep + 1);
  }
  const url = tail && tail.startsWith('http') ? tail : undefined;
  return {
    id: trimmed,
    form,
    filedAt,
    filedAtLabel: formatFilingDate(filedAt),
    url,
    reference: url ? null : tail || null,
    sortKey: filedAt ? Date.parse(filedAt) : null,
  };
};

const buildVersionSummaries = (history?: ExposureHistoryEntry[]): VersionSummary[] => {
  if (!Array.isArray(history) || history.length === 0) return [];
  return history
    .map((record) => {
      const version = record?.version ?? 'unknown';
      const updatedAt = record?.updatedAt ?? record?.createdAt ?? null;
      const filings = (record?.sources ?? [])
        .map((src) => parseSecSource(src))
        .filter((entry): entry is FilingEntry => Boolean(entry))
        .map((entry) => ({ ...entry, version }))
        .slice(0, 6);
      return {
        version,
        updatedAt,
        updatedAtLabel: formatVersionTimestamp(updatedAt),
        filings,
      };
    })
    .filter(Boolean);
};

const buildNormalizedFilings = (summaries: VersionSummary[]): NormalizedFiling[] => {
  const map = new Map<string, NormalizedFiling>();
  for (const summary of summaries) {
    for (const filing of summary.filings) {
      const uniqueKey = `${filing.form}|${filing.filedAt ?? ''}|${filing.url ?? filing.reference ?? ''}`;
      const existing = map.get(uniqueKey);
      if (!existing) {
        map.set(uniqueKey, {
          ...filing,
          dedupeKey: uniqueKey,
          versions: filing.version ? [filing.version] : [],
        });
        continue;
      }
      if (filing.version && !existing.versions.includes(filing.version)) {
        existing.versions = [...existing.versions, filing.version];
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    if (a.sortKey == null && b.sortKey == null) return 0;
    if (a.sortKey == null) return 1;
    if (b.sortKey == null) return -1;
    return b.sortKey - a.sortKey;
  });
};

const renderFilingPrimary = (filing: FilingEntry): React.ReactNode => {
  const title = `Form ${filing.form}`;
  return filing.url ? (
    <MuiLink href={filing.url} target="_blank" rel="noreferrer">{title}</MuiLink>
  ) : (
    title
  );
};

export const ProfileExposurePanel: React.FC<Props> = ({ profileId, ticker, instrumentId }) => {
  const resolvedInstrumentId = React.useMemo(() =>
    resolveInstrumentId({ instrumentId: instrumentId || undefined, profileId: profileId || undefined, ticker: ticker || undefined })
  , [instrumentId, profileId, ticker]);
  const exposureState = useExposureEntry(resolvedInstrumentId, { includeHistory: true, historyLimit: 12 });
  const versionSummaries = React.useMemo(() => buildVersionSummaries(exposureState.history), [exposureState.history]);
  const normalizedFilings = React.useMemo(
    () => buildNormalizedFilings(versionSummaries).slice(0, 8),
    [versionSummaries]
  );

  const historyLoading = exposureState.loading && versionSummaries.length === 0;
  const filingsError = exposureState.error;

  if (!profileId) return null;

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader title="Exposures & Filings" subheader="Latest SEC anchors and exposure snapshot" />
      <Divider />
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Stack sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="subtitle2" gutterBottom>Exposure snapshot</Typography>
            {resolvedInstrumentId ? (
              <ExposureSnapshot instrumentId={resolvedInstrumentId} state={exposureState} />
            ) : (
              <Typography variant="body2" color="text.secondary">No instrument resolved.</Typography>
            )}
          </Stack>
          <Stack sx={{ flex: 1, minWidth: 320 }} spacing={1.5}>
            <Stack>
              <Typography variant="subtitle2" gutterBottom>SEC filings feed</Typography>
              {!resolvedInstrumentId ? (
                <Typography variant="body2" color="text.secondary">Provide a ticker or instrument to surface filings.</Typography>
              ) : historyLoading ? (
                <Typography variant="body2" color="text.secondary">Loading SEC filings…</Typography>
              ) : filingsError ? (
                <Typography variant="body2" color="error">{filingsError}</Typography>
              ) : normalizedFilings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No SEC filings recorded yet.</Typography>
              ) : (
                <List dense>
                  {normalizedFilings.map((filing) => {
                    return (
                      <ListItem key={filing.dedupeKey} disableGutters>
                        <ListItemText
                          primary={renderFilingPrimary(filing)}
                          secondary={(
                            <Stack spacing={0.25}>
                              {filing.filedAtLabel ? (
                                <Typography variant="caption" color="text.secondary">
                                  Filed {filing.filedAtLabel}
                                </Typography>
                              ) : null}
                              {!filing.url && filing.reference ? (
                                <Typography variant="caption" color="text.secondary">
                                  {filing.reference}
                                </Typography>
                              ) : null}
                              {filing.versions.length > 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  Used in versions {filing.versions.join(', ')}
                                </Typography>
                              ) : null}
                            </Stack>
                          )}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Stack>

            <Divider flexItem />

            <Stack>
              <Typography variant="subtitle2" gutterBottom>Exposure versions</Typography>
              {!resolvedInstrumentId ? (
                <Typography variant="body2" color="text.secondary">Provide a ticker or instrument to view exposure history.</Typography>
              ) : historyLoading ? (
                <Typography variant="body2" color="text.secondary">Loading version history…</Typography>
              ) : filingsError ? (
                <Typography variant="body2" color="error">{filingsError}</Typography>
              ) : versionSummaries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No exposure versions have been stewarded yet.</Typography>
              ) : (
                <Stack spacing={1}>
                  {versionSummaries.map((summary) => (
                    <Stack
                      key={summary.version}
                      spacing={0.5}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
                          Version {summary.version}
                        </Typography>
                        {summary.updatedAtLabel ? (
                          <Typography variant="caption" color="text.secondary">
                            Updated {summary.updatedAtLabel}
                          </Typography>
                        ) : null}
                      </Stack>
                      {summary.filings.length > 0 ? (
                        <Stack spacing={0.5}>
                          {summary.filings.map((filing) => (
                            <Stack key={`${summary.version}-${filing.id}`} spacing={0}>
                              <Typography variant="body2" component="div">
                                {renderFilingPrimary(filing)}
                              </Typography>
                              {filing.filedAtLabel ? (
                                <Typography variant="caption" color="text.secondary">
                                  Filed {filing.filedAtLabel}
                                </Typography>
                              ) : null}
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No SEC anchors captured in this version.
                        </Typography>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
